<!-- Ported from the Polaris kit: src/components/ui/command/Command.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs.
     Change the design in the kit first, then re-port.
     Hand change (phase P): `filterItems` loops rewritten as array iterations (this repo's lint forbids for…of and
     ++); same result. A re-port brings the loops back. -->
<script setup>
import { reactiveOmit } from '@vueuse/core';
import { ListboxRoot, useFilter, useForwardPropsEmits } from 'reka-ui';
import { reactive, ref, watch } from 'vue';
import { cn } from '../../lib/utils';
import { provideCommandContext } from '.';

const props = defineProps({
  modelValue: { type: null, required: false, default: '' },
  defaultValue: { type: null, required: false },
  multiple: { type: Boolean, required: false },
  orientation: { type: String, required: false },
  dir: { type: String, required: false },
  disabled: { type: Boolean, required: false },
  selectionBehavior: { type: String, required: false },
  highlightOnHover: { type: Boolean, required: false, default: true },
  by: { type: [String, Function], required: false },
  asChild: { type: Boolean, required: false },
  as: { type: null, required: false },
  name: { type: String, required: false },
  required: { type: Boolean, required: false },
  class: {
    type: [Boolean, null, String, Object, Array],
    required: false,
    skipCheck: true,
  },
});

const emits = defineEmits([
  'update:modelValue',
  'highlight',
  'entryFocus',
  'leave',
]);

const delegatedProps = reactiveOmit(props, 'class');

const forwarded = useForwardPropsEmits(delegatedProps, emits);

const allItems = ref(new Map());
const allGroups = ref(new Map());

const { contains } = useFilter({ sensitivity: 'base' });
const filterState = reactive({
  search: '',
  filtered: {
    /** The count of all visible items. */
    count: 0,
    /** Map from visible item id to its search score. */
    items: new Map(),
    /** Set of groups with at least one visible item. */
    groups: new Set(),
  },
});

function filterItems() {
  if (!filterState.search) {
    filterState.filtered.count = allItems.value.size;
    // Do nothing, each item will know to show itself because search is empty
    return;
  }

  // Reset the groups
  filterState.filtered.groups = new Set();

  // Check which items should be included
  const itemCount = [...allItems.value].filter(([id, value]) => {
    const score = contains(value, filterState.search);
    filterState.filtered.items.set(id, score ? 1 : 0);
    return score;
  }).length;

  // Check which groups have at least 1 item shown
  [...allGroups.value].forEach(([groupId, group]) => {
    if ([...group].some(itemId => filterState.filtered.items.get(itemId) > 0))
      filterState.filtered.groups.add(groupId);
  });

  filterState.filtered.count = itemCount;
}

watch(
  () => filterState.search,
  () => {
    filterItems();
  }
);

provideCommandContext({
  allItems,
  allGroups,
  filterState,
});
</script>

<template>
  <ListboxRoot
    data-slot="command"
    v-bind="forwarded"
    :class="
      cn(
        'bg-polaris-popover text-polaris-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-polaris-md',
        props.class
      )
    "
  >
    <slot />
  </ListboxRoot>
</template>
